import * as Models from "app/services/proxy/model/models";
import {Lookup} from "framework/services/utils.service";
import * as LDClient from "launchdarkly-js-client-sdk";

export class FeatureFlagsHelper
{
    // Error messages
    private static readonly ERR_UNSUPPORTED_FLAG_TYPE         = (flagName: string,
                                                                 flagType: string) =>
        `Unsupported flag type '${flagType}' in flag '${flagName}'. Only 'A' and 'F' types are supported.`;
    private static readonly ERR_INVALID_FLAG_TYPE             = "Invalid flag type!";
    private static readonly ERR_FEATURE_CATEGORY_NOT_PROVIDED = "Feature category not provided in request";
    private static readonly ERR_FLAG_CATEGORY_REQUIRED        = "Flag category is required in the request";

    // Warning messages
    private static readonly WARN_UNKNOWN_CATEGORY = (category: string,
                                                     flagName: string) =>
        `Unknown flag category '${category}' for flag '${flagName}'`;

    //--// Constants
    private static readonly LD_CONTEXT_KIND_USER = "user";
    private static readonly LD_DEFAULT_USER_KEY  = "anonymous";
    private static readonly FLAG_PREFIX_RELEASE  = "release";
    private static readonly FLAG_NAME_DELIMITER  = "_";
    private static readonly FLAG_TYPE_INDEX      = 2;
    private static readonly MIN_FLAG_PARTS       = 3;

    //--// Public API (alphabetical)

    public static createFeatureFlag(category: Models.FeatureFlagCategory,
                                    flagName: string,
                                    flagValue: boolean,
                                    defaultValue: boolean): Models.FeatureFlag
    {
        return Models.FeatureFlag.newInstance({
                                                  category : category,
                                                  flag_name: flagName,
                                                  enabled  : flagValue ?? defaultValue,
                                                  default  : defaultValue,
                                                  flag_type: FeatureFlagsHelper.extractFlagType(flagName)
                                              });
    }

    public static createReleaseFlag(category: Models.FeatureFlagCategory,
                                    flagName: string,
                                    flagValue: boolean,
                                    defaultValue: boolean): Models.ReleaseFlag
    {
        return Models.ReleaseFlag.newInstance({
                                                  category      : category,
                                                  flag_name     : flagName,
                                                  enabled       : flagValue ?? defaultValue,
                                                  default       : defaultValue,
                                                  release_prefix: FeatureFlagsHelper.extractReleasePrefix(flagName),
                                                  release_id    : FeatureFlagsHelper.extractReleaseId(flagName)
                                              });
    }

    public static extractFlagType(flagName: string): Models.FeatureFlagType
    {
        // Flag name format: {prefix}_{version}_{type}_{description}
        // Example: ai_2504_a_test_flag, rept_2504_f_test_flag
        const parts = flagName.split(FeatureFlagsHelper.FLAG_NAME_DELIMITER);
        if (parts.length < FeatureFlagsHelper.MIN_FLAG_PARTS) return null;

        const typeChar = parts[FeatureFlagsHelper.FLAG_TYPE_INDEX].toUpperCase();
        switch (typeChar)
        {
            case Models.FeatureFlagType.F:
                return Models.FeatureFlagType.F;

            case Models.FeatureFlagType.B:
                return Models.FeatureFlagType.B;

            case Models.FeatureFlagType.A:
                return Models.FeatureFlagType.A;

            default:
                throw new Error(FeatureFlagsHelper.ERR_INVALID_FLAG_TYPE);
        }
    }

    public static extractReleaseId(flagName: string): string
    {
        // Release flag name format: release_{id}
        // Example: release_2504
        const parts = flagName.split(FeatureFlagsHelper.FLAG_NAME_DELIMITER);
        if (parts.length >= 2 && parts[0].toLowerCase() === FeatureFlagsHelper.FLAG_PREFIX_RELEASE)
        {
            return parts.slice(1)
                        .join(FeatureFlagsHelper.FLAG_NAME_DELIMITER);
        }
        return null;
    }

    public static extractReleasePrefix(flagName: string): Models.ReleasePrefix
    {
        return FeatureFlagsHelper.isReleaseFlag(flagName) ? Models.ReleasePrefix.RELEASE : null;
    }

    public static hasContextChanged(oldContext: LDClient.LDContext | null,
                                    newContext: LDClient.LDContext): boolean
    {
        if (!oldContext) return true;

        // Compare key fields
        if (oldContext.key !== newContext.key) return true;
        if (oldContext.email !== newContext.email) return true;

        // Compare custom attributes
        const currentCustom = oldContext.custom || {};
        const newCustom     = newContext.custom || {};

        return JSON.stringify(currentCustom) !== JSON.stringify(newCustom);
    }

    public static isReleaseFlag(flagName: string): boolean
    {
        return flagName.toLowerCase()
                       .startsWith(FeatureFlagsHelper.FLAG_PREFIX_RELEASE);
    }

    public static isSupportedFlagType(flagType: Models.FeatureFlagType): boolean
    {
        return flagType === Models.FeatureFlagType.A || flagType === Models.FeatureFlagType.F;
    }

    public static translateToLDContext(context: Models.FeatureFlagContext): LDClient.LDContext
    {
        return {
            kind  : FeatureFlagsHelper.LD_CONTEXT_KIND_USER,
            key   : context.user_id || FeatureFlagsHelper.LD_DEFAULT_USER_KEY,
            email : context.email,
            custom:
                {
                    masterAccountId: context.master_account_id,
                    tenantId       : context.tenant_id,
                    roleId         : context.role_id,
                    groupId        : context.group_id,
                    ipAddress      : context.ip_address,
                    country        : context.country,
                    region         : context.region
                }
        };
    }

    public static validateAllFlagsRequest(request: Models.FeatureFlagRequestBase): void
    {
        if (request instanceof Models.FeatureFlagRequest)
        {
            if (!request.feature_category)
            {
                throw new Error(FeatureFlagsHelper.ERR_FEATURE_CATEGORY_NOT_PROVIDED);
            }
        }
    }

    public static buildDefaultsLookup(...instances: (Models.FeatureFlagsWithDefaults | Models.ReleaseFlagsWithDefaults)[]): Lookup<Models.FeatureFlagBase>
    {
        const lookup: Lookup<Models.FeatureFlagBase> = {};
        for (const instance of instances)
        {
            for (const [flagName, value] of Object.entries(instance))
            {
                if (typeof value !== "boolean") continue;

                lookup[flagName] = FeatureFlagsHelper.isReleaseFlag(flagName)
                    ? FeatureFlagsHelper.createReleaseFlag(Models.FeatureFlagCategory.RELEASE, flagName, value, value)
                    : FeatureFlagsHelper.createFeatureFlag(Models.FeatureFlagCategory.FEATURE, flagName, value, value);
            }
        }
        return lookup;
    }

    public static validateFlagRequest(request: Models.FeatureFlagRequestBase): void
    {
        if (!request.category)
        {
            throw new Error(FeatureFlagsHelper.ERR_FLAG_CATEGORY_REQUIRED);
        }

        if (request.category !== Models.FeatureFlagCategory.FEATURE && request.category !== Models.FeatureFlagCategory.RELEASE)
        {
            throw new Error(FeatureFlagsHelper.WARN_UNKNOWN_CATEGORY(request.category, request.flag_name));
        }

        // Validate flag type for feature flags (only 'A' and 'F' are supported)
        if (request.category === Models.FeatureFlagCategory.FEATURE && request.flag_name)
        {
            const flagType = FeatureFlagsHelper.extractFlagType(request.flag_name);
            if (!FeatureFlagsHelper.isSupportedFlagType(flagType))
            {
                const extractedType = flagType ? flagType.toString() : "unknown";
                throw new Error(FeatureFlagsHelper.ERR_UNSUPPORTED_FLAG_TYPE(request.flag_name, extractedType));
            }
        }
    }
}
